/**
 * MarkPaidDialog — Modale de marquage "payé" avec date manuellement configurable.
 *
 * Règle métier : les salaires du mois M sont payés le 10 du mois M+1 (date par défaut).
 * L'utilisateur peut modifier cette date lors du marquage.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, CalendarDays, X, Wallet } from "lucide-react";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const fmt = (v) => (Math.round(v || 0)).toLocaleString("fr-FR");

/**
 * Calcule la date par défaut de paiement : le 10 du mois M+1.
 */
export function defaultPaymentDate(year, month, day = 10) {
  const y = month === 12 ? year + 1 : year;
  const m = month === 12 ? 1 : month + 1;
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm - reçoit ({ payment_date, amount, paid_by, notes })
 * @param {number} props.year - mois de travail
 * @param {number} props.month - mois de travail
 * @param {string} props.employeeName
 * @param {number} props.suggestedAmount - montant proposé (net calculé)
 * @param {string} props.paidBy - nom de l'utilisateur qui marque payé
 */
const MarkPaidDialog = ({
  open, onClose, onConfirm,
  year, month, employeeName = "—",
  suggestedAmount = 0, paidBy = "Administrateur",
}) => {
  const [payDate, setPayDate] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPayDate(defaultPaymentDate(year, month));
      setAmount(String(Math.round(suggestedAmount || 0)));
      setNotes("");
    }
  }, [open, year, month, suggestedAmount]);

  const submit = async () => {
    if (!payDate) return;
    setSaving(true);
    try {
      await onConfirm({
        payment_date: payDate,
        amount: parseFloat(amount) || 0,
        paid_by: paidBy,
        notes,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const workMonthLabel = `${MONTHS[month - 1]} ${year}`;
  const defaultDate = defaultPaymentDate(year, month);
  const isCustomDate = payDate !== defaultDate;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md" data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-300">
            <Wallet className="w-5 h-5" />
            Marquer comme payé
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Enregistrer le paiement effectif du net de <b className="text-slate-200">{employeeName}</b>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Contexte règle métier */}
          <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 text-[12px]">
            <div className="flex items-center gap-2 text-slate-300 mb-1">
              <CalendarDays className="w-3.5 h-3.5 text-blue-300" />
              <span className="font-bold">Mois travaillé :</span>
              <span className="text-white">{workMonthLabel}</span>
            </div>
            <p className="text-slate-500 text-[10.5px] leading-tight">
              Règle : le net du mois <b className="text-slate-300">{workMonthLabel}</b> est payé
              le <b className="text-emerald-300">{new Date(defaultDate).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</b> par défaut.
              Vous pouvez modifier cette date.
            </p>
          </div>

          <div>
            <Label className="text-slate-300 text-[11px]">Date de paiement effective *</Label>
            <Input
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              className="bg-slate-700/60 border-slate-600 text-white h-10"
              data-testid="mark-paid-date-input"
            />
            {isCustomDate && (
              <button
                type="button"
                onClick={() => setPayDate(defaultDate)}
                className="text-[10.5px] text-blue-300 hover:text-blue-200 underline decoration-dotted mt-1"
              >
                ⟲ Restaurer la date par défaut (10 du mois suivant)
              </button>
            )}
          </div>

          <div>
            <Label className="text-slate-300 text-[11px]">Montant versé (F CFA)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-slate-700/60 border-slate-600 text-white h-10 font-mono text-[15px] font-bold"
              placeholder="0"
              data-testid="mark-paid-amount-input"
            />
            {suggestedAmount > 0 && parseFloat(amount) !== Math.round(suggestedAmount) && (
              <button
                type="button"
                onClick={() => setAmount(String(Math.round(suggestedAmount)))}
                className="text-[10.5px] text-blue-300 hover:text-blue-200 underline decoration-dotted mt-1"
              >
                ⟲ Utiliser le net calculé ({fmt(suggestedAmount)} F)
              </button>
            )}
          </div>

          <div>
            <Label className="text-slate-300 text-[11px]">Notes / référence (optionnel)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-slate-700/60 border-slate-600 text-white h-9"
              placeholder="Ex : virement UBA, chèque #123…"
              data-testid="mark-paid-notes-input"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              onClick={submit}
              disabled={saving || !payDate}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-10"
              data-testid="mark-paid-confirm-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {saving ? "Enregistrement…" : "Confirmer le paiement"}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="border-slate-600 text-slate-300 h-10"
            >
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MarkPaidDialog;
